/**
 * PKCE (Proof Key for Code Exchange) utility module.
 *
 * Generates cryptographically secure code verifier and code challenge values
 * for the OAuth 2.0 Authorization Code + PKCE flow using the Web Crypto API.
 */

/** Unreserved URI character set per RFC 3986 */
const UNRESERVED_CHARS =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

/** Default verifier length (within the 43–128 range) */
const VERIFIER_LENGTH = 128;

/**
 * Generates a cryptographically random code verifier string.
 * Length: 43–128 characters, using unreserved URI characters [A-Za-z0-9\-._~].
 *
 * Uses crypto.getRandomValues for cryptographically secure random values.
 */
export async function generateCodeVerifier(): Promise<string> {
  const randomValues = new Uint8Array(VERIFIER_LENGTH);
  crypto.getRandomValues(randomValues);

  let verifier = '';
  for (let i = 0; i < VERIFIER_LENGTH; i++) {
    verifier += UNRESERVED_CHARS[randomValues[i] % UNRESERVED_CHARS.length];
  }

  return verifier;
}

/**
 * Generates a code challenge from a code verifier using SHA-256 + Base64-URL encoding.
 * Deterministic: the same verifier always produces the same challenge.
 *
 * Steps:
 * 1. Encode the verifier as UTF-8
 * 2. Compute SHA-256 hash using Web Crypto API
 * 3. Base64-URL encode the hash (replace + with -, / with _, remove trailing =)
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  const hashArray = new Uint8Array(hashBuffer);
  let base64 = '';
  for (let i = 0; i < hashArray.length; i++) {
    base64 += String.fromCharCode(hashArray[i]);
  }
  base64 = btoa(base64);

  // Convert to Base64-URL: replace + with -, / with _, remove trailing =
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Convenience function that generates both a code verifier and its corresponding
 * code challenge.
 */
export async function generatePKCEPair(): Promise<{
  codeVerifier: string;
  codeChallenge: string;
}> {
  const codeVerifier = await generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  return { codeVerifier, codeChallenge };
}
